import { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { MODULES } from "../utils";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, KeyRound } from "lucide-react";

const EMPTY = { name: "", email: "", role: "employe", specialty: "nettoyage", active: true };

export default function Employees() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, active: form.active };
        payload.specialty = form.role === "employe" ? form.specialty : null;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Compte modifié");
      } else {
        const r = await api.post("/users", { name: form.name, email: form.email, role: form.role, specialty: form.role === "employe" ? form.specialty : null });
        if (r.data.invite_email_sent === false) {
          toast.warning("Compte créé, mais l'invitation n'a pas pu être envoyée. Renvoyez-la depuis la liste");
        } else {
          toast.success("Compte créé — invitation envoyée par email");
        }
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de « ${u.name} » ?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Compte supprimé"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/resend-invitation`); toast.success(`Invitation renvoyée à ${u.email}`); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const sendPasswordLink = async (u) => {
    if (!window.confirm(`Envoyer un lien de définition de mot de passe à ${u.email} ?`)) return;
    try { await api.post(`/users/${u.id}/password-link`); toast.success(`Lien de mot de passe envoyé à ${u.email}`); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Employés & accès</h1>
        <button data-testid="create-employee-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
          className="flex items-center gap-2 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors">
          <Plus size={16} /> Nouveau compte
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">Nom</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Rôle</th>
              <th className="px-5 py-3">Spécialité</th><th className="px-5 py-3">Statut</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="employees-table">
            {users.map((u) => (
              <tr key={u.id} data-testid={`employee-row-${u.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-5 py-3 text-slate-600">{u.email}</td>
                <td className="px-5 py-3 capitalize text-slate-600">{u.role}</td>
                <td className="px-5 py-3">{u.specialty ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${MODULES[u.specialty]?.badge}`}>{MODULES[u.specialty]?.label}</span> : <span className="text-slate-400">—</span>}</td>
                <td className="px-5 py-3" data-testid={`employee-status-${u.id}`}>
                  {u.invite_pending
                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Invitation en attente</span>
                    : u.active !== false
                      ? <span className="text-emerald-600 font-semibold text-xs">Actif</span>
                      : <span className="text-red-500 font-semibold text-xs">Désactivé</span>}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {u.invite_pending && (
                    <button data-testid={`resend-invitation-${u.id}`} title="Renvoyer l'invitation" onClick={() => resendInvite(u)}
                      className="text-slate-400 hover:text-slate-900 p-1"><Mail size={15} /></button>
                  )}
                  {!u.invite_pending && (
                    <button data-testid={`password-link-${u.id}`} title="Envoyer un lien de mot de passe" onClick={() => sendPasswordLink(u)}
                      className="text-slate-400 hover:text-slate-900 p-1"><KeyRound size={15} /></button>
                  )}
                  <button data-testid={`edit-employee-${u.id}`} onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, role: u.role, specialty: u.specialty || "nettoyage", active: u.active !== false }); setOpen(true); }}
                    className="text-slate-400 hover:text-slate-900 p-1"><Pencil size={15} /></button>
                  <button data-testid={`delete-employee-${u.id}`} onClick={() => remove(u)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="employee-form-modal">
          <form onSubmit={save} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">{editing ? `Modifier ${editing.name}` : "Nouveau compte"}</h2>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom *</label>
              <input data-testid="employee-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
            {!editing && (
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email *</label>
                <input data-testid="employee-form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            )}
            {!editing && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2" data-testid="employee-form-invite-info">
                Aucun mot de passe à saisir : un email d'invitation sera envoyé à cette adresse pour que la personne choisisse son mot de passe (lien valable 48 heures).
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rôle</label>
                <select data-testid="employee-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="employe">Employé</option><option value="superviseur">Superviseur</option><option value="admin">Admin</option>
                </select>
              </div>
              {form.role === "employe" && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Spécialité</label>
                  <select data-testid="employee-form-specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                    {Object.entries(MODULES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                  </select>
                </div>
              )}
            </div>
            {editing && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input data-testid="employee-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Compte actif
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="employee-form-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="employee-form-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">
                {editing ? "Enregistrer" : "Créer et envoyer l'invitation"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
